import React, { useEffect, useMemo, useState } from 'react';
import { collection, doc, getDocs, query, runTransaction, where } from 'firebase/firestore';
import { ArrowsRightLeftIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { db } from '../firebase';
import { Button } from './button';
import { Text } from './text';
import { Dialog, DialogTitle, DialogBody, DialogActions } from './dialog';
import { useUser } from './UserContext';
import { logBitacora } from '../utils/bitacora';

// Move a convenio contract to another proyecto of the SAME convenio. Everything
// derives from contracts/{id}.proyectoId (rules, storage rules, portal pages,
// Cobertura por proyecto), so the move is that one field plus keeping the
// convenio's proyectos[].contracts list in step, in one transaction. Fianzas
// live on the contract and the ledger never stored proyectoId, so no money
// moves. Cross-convenio is out: the contract id and its storage path are
// bound to the convenio.

const SIN_PROYECTO = '__sin_proyecto__';

export function MoverProyectoDialog({ open, onClose, contrato, convenio, onMoved }) {
  const { user, userData } = useUser();
  const [destino, setDestino] = useState('');
  const [portalUsers, setPortalUsers] = useState([]);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState(null);

  const convenioId = convenio?.id || contrato?.convenioId || null;
  const proyectos = useMemo(() => convenio?.proyectos || [], [convenio]);
  const actualId = contrato?.proyectoId || null;
  const actual = proyectos.find((p) => p.id === actualId) || null;

  useEffect(() => {
    if (!open) return;
    setDestino('');
    setError(null);
  }, [open, contrato?.id]);

  // Portal users with partial access to this convenio: the only ones whose
  // visibility changes with the move (fullAccess users see every proyecto).
  useEffect(() => {
    if (!open || !convenioId || !userData?.org) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(
          query(collection(db, 'usuarios'), where('org', '==', userData.org), where('rol', '==', 'client'))
        );
        const users = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((u) => u.convenioAccess && convenioId in u.convenioAccess);
        if (!cancelled) setPortalUsers(users);
      } catch (err) {
        console.error('Error loading portal users for move:', err);
        if (!cancelled) setPortalUsers([]);
      }
    })();
    return () => { cancelled = true; };
  }, [open, convenioId, userData?.org]);

  const destinoId = destino === SIN_PROYECTO ? null : destino || null;
  const destinoNombre = destino === SIN_PROYECTO
    ? 'Sin proyecto'
    : proyectos.find((p) => p.id === destino)?.nombre || '';

  const impacto = useMemo(() => {
    if (!destino) return { ganan: [], pierden: [] };
    const ve = (u, pid) => {
      const acc = u.convenioAccess?.[convenioId];
      if (!acc) return false;
      if (acc.fullAccess) return true;
      return Boolean(pid) && (acc.proyectoIds || []).includes(pid);
    };
    const ganan = [];
    const pierden = [];
    portalUsers.forEach((u) => {
      const antes = ve(u, actualId);
      const despues = ve(u, destinoId);
      if (!antes && despues) ganan.push(u);
      if (antes && !despues) pierden.push(u);
    });
    return { ganan, pierden };
  }, [destino, destinoId, actualId, portalUsers, convenioId]);

  const handleMove = async () => {
    if (!contrato?.id || !convenioId || !destino) return;
    setWorking(true);
    setError(null);
    try {
      await runTransaction(db, async (tx) => {
        const contratoRef = doc(db, 'contracts', contrato.id);
        const convenioRef = doc(db, 'convenios', convenioId);
        const [cSnap, vSnap] = await Promise.all([tx.get(contratoRef), tx.get(convenioRef)]);
        if (!cSnap.exists()) throw new Error('Contrato no encontrado.');
        if (!vSnap.exists()) throw new Error('Convenio no encontrado.');
        const cData = cSnap.data();
        if (cData.convenioId !== convenioId) throw new Error('El contrato no pertenece a este convenio.');
        if (destinoId && !(vSnap.data().proyectos || []).some((p) => p.id === destinoId)) {
          throw new Error('El proyecto destino ya no existe en el convenio.');
        }
        const nuevos = (vSnap.data().proyectos || []).map((p) => {
          const lista = (p.contracts || []).filter((id) => id !== contrato.id);
          if (p.id === destinoId) lista.push(contrato.id);
          return { ...p, contracts: lista };
        });
        tx.update(contratoRef, { proyectoId: destinoId, updatedAt: Date.now() });
        tx.update(convenioRef, { proyectos: nuevos });
      });

      try {
        await logBitacora({
          action: 'UPDATE',
          collection: 'contracts',
          documentId: contrato.id,
          changes: [{ field: 'proyectoId', oldValue: actualId, newValue: destinoId }],
          metadata: {
            convenioId,
            description: `Movió el contrato "${contrato.tituloContrato?.value || 'Sin título'}" de ${
              actual?.nombre || 'Sin proyecto'
            } a ${destinoNombre || 'Sin proyecto'}`,
            proyectoAnterior: actual?.nombre || null,
            proyectoNuevo: destinoNombre || null,
          },
          user,
          userData,
        });
      } catch (logErr) {
        console.error('Failed to log proyecto move to bitacora:', logErr);
      }

      if (onMoved) onMoved(destinoId);
      onClose();
    } catch (err) {
      console.error('Error moving contract between proyectos:', err);
      setError(err.message || 'No se pudo mover el contrato.');
    } finally {
      setWorking(false);
    }
  };

  const opciones = proyectos.filter((p) => p.id !== actualId);
  const nombreUsuario = (u) => u.nombre || u.correo || u.id;

  return (
    <Dialog open={open} onClose={() => !working && onClose()} size="md">
      <DialogTitle className="flex items-center gap-2">
        <ArrowsRightLeftIcon className="h-5 w-5 text-azul-surelia" />
        Mover a otro proyecto
      </DialogTitle>
      <DialogBody className="space-y-4">
        <Text>
          Proyecto actual: <strong>{actual?.nombre || 'Sin proyecto'}</strong>. Las fianzas y el libro de comisiones
          no cambian; solo quién lo ve en el portal del convenio.
        </Text>

        {opciones.length === 0 && actualId === null ? (
          <Text className="text-zinc-500">Este convenio no tiene otros proyectos.</Text>
        ) : (
          <div>
            <label className="block text-xs font-medium text-zinc-600 mb-1">Proyecto destino</label>
            <select
              value={destino}
              onChange={(e) => setDestino(e.target.value)}
              disabled={working}
              className="w-full px-3 py-2 border border-azul-surelia/30 rounded-lg text-sm bg-white focus:border-azul-surelia focus:ring-2 focus:ring-azul-surelia/20 outline-none"
            >
              <option value="">Elige un proyecto</option>
              {opciones.map((p) => (
                <option key={p.id} value={p.id}>{p.nombre || p.id}</option>
              ))}
              {actualId !== null && <option value={SIN_PROYECTO}>Sin proyecto</option>}
            </select>
          </div>
        )}

        {destino && (impacto.ganan.length > 0 || impacto.pierden.length > 0) && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2 text-sm">
            <p className="font-semibold text-amber-800">Visibilidad en el portal</p>
            {impacto.pierden.length > 0 && (
              <div className="flex gap-2">
                <EyeSlashIcon className="h-4 w-4 text-amber-700 flex-shrink-0 mt-0.5" />
                <div className="text-amber-800">
                  <span className="font-medium">Dejan de verlo:</span>{' '}
                  {impacto.pierden.map(nombreUsuario).join(', ')}
                </div>
              </div>
            )}
            {impacto.ganan.length > 0 && (
              <div className="flex gap-2">
                <EyeIcon className="h-4 w-4 text-amber-700 flex-shrink-0 mt-0.5" />
                <div className="text-amber-800">
                  <span className="font-medium">Empiezan a verlo:</span>{' '}
                  {impacto.ganan.map(nombreUsuario).join(', ')}
                </div>
              </div>
            )}
          </div>
        )}
        {destino && impacto.ganan.length === 0 && impacto.pierden.length === 0 && (
          <Text className="text-xs text-zinc-500">Ningún usuario del portal cambia de visibilidad.</Text>
        )}

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
        )}
      </DialogBody>
      <DialogActions>
        <Button outline color="surelia" onClick={onClose} disabled={working}>Cancelar</Button>
        <Button color="surelia" onClick={handleMove} disabled={working || !destino}>
          {working ? 'Moviendo...' : 'Mover contrato'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default MoverProyectoDialog;
